
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from 'uuid';
import { SessionVerification, VerificationStep } from "@/types/verificationTypes";

// Check if verification tracking is enabled
export const isVerificationTrackingEnabled = (): boolean => {
  try {
    // Always enable in development for better debugging
    if (process.env.NODE_ENV === 'development') {
      return true;
    }
    
    // In production, check admin settings
    const adminSettings = localStorage.getItem('ADMIN_SETTINGS');
    if (!adminSettings) return true; // Default to enabled for better support
    
    const settings = JSON.parse(adminSettings);
    return settings?.verification_tracking?.enabled !== false; // Enabled unless explicitly disabled
  } catch (error) {
    console.error("[VerificationService] Error checking if verification tracking is enabled:", error);
    return true; // Default to enabled for better debugging
  }
};

// Start a verification session
export const startVerificationSession = async (
  userEmail: string | null, 
  type: string, 
  initialData: Record<string, any> = {},
  label: string = 'Verification Session'
): Promise<string> => {
  try {
    if (!isVerificationTrackingEnabled()) {
      console.log("[VerificationService] Verification tracking is disabled, skipping session creation");
      return "";
    }
    
    const sessionId = uuidv4();
    const timestamp = new Date().toISOString();
    
    console.log(`[VerificationService] Starting new verification session: ${sessionId}`, { 
      userEmail, 
      type, 
      label 
    });
    
    // Create session record
    const { error } = await supabase
      .from('auth_verification_sessions')
      .insert({
        session_id: sessionId,
        success: false, // Will be updated later
        start_timestamp: timestamp,
        last_timestamp: timestamp,
        step_count: 0,
        user_email: userEmail || null,
        type: type || 'verification',
        data: initialData || {},
        label: label || 'Unlabeled Session'
      });
      
    if (error) {
      console.error("[VerificationService] Error creating verification session:", error);
      return "";
    }
    
    return sessionId;
  } catch (error) {
    console.error("[VerificationService] Error in startVerificationSession:", error);
    return "";
  }
};

// Record a verification step
export const recordVerificationStep = async (
  sessionId: string,
  step: string,
  status: "info" | "success" | "error" | "warning",
  details: Record<string, any> = {}
): Promise<boolean> => {
  try {
    if (!isVerificationTrackingEnabled() || !sessionId) {
      return false;
    }
    
    const timestamp = new Date().toISOString();
    const stepId = uuidv4();
    
    // Insert step record
    const { error: stepError } = await supabase
      .from('auth_verification_steps')
      .insert({
        id: stepId,
        session_id: sessionId,
        step: step,
        status: status,
        details: details || null,
        timestamp
      });
      
    if (stepError) {
      console.error("[VerificationService] Error recording verification step:", stepError);
      return false;
    }
    
    // Get current step count from the database
    const { data: sessionData } = await supabase
      .from('auth_verification_sessions')
      .select('step_count')
      .eq('session_id', sessionId)
      .single();
    
    const currentStepCount = sessionData?.step_count || 0;
    
    // Update session record
    const { error: sessionError } = await supabase
      .from('auth_verification_sessions')
      .update({
        last_timestamp: timestamp,
        step_count: currentStepCount + 1,
        success: status === 'success' ? true : undefined // Only update to true, never to false
      })
      .eq('session_id', sessionId);
      
    if (sessionError) {
      console.error("[VerificationService] Error updating verification session:", sessionError);
    }
    
    return true;
  } catch (error) {
    console.error("[VerificationService] Error in recordVerificationStep:", error);
    return false;
  }
};

// Complete a verification session
export const completeVerificationSession = async (
  sessionId: string,
  success: boolean,
  finalData: Record<string, any> = {}
): Promise<boolean> => {
  try {
    if (!isVerificationTrackingEnabled() || !sessionId) {
      return false;
    }
    
    console.log(`[VerificationService] Completing session ${sessionId}`, { success });
    
    // Update session status
    const { error } = await supabase
      .from('auth_verification_sessions')
      .update({
        success,
        last_timestamp: new Date().toISOString(),
        data: finalData
      })
      .eq('session_id', sessionId);
      
    if (error) {
      console.error("[VerificationService] Error completing verification session:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("[VerificationService] Error in completeVerificationSession:", error);
    return false;
  }
};

// Get all sessions with optional filtering
export const getSessions = async (statusFilter: string = "all", labelFilter?: string): Promise<{sessions: SessionVerification[], totalCount: number}> => {
  try {
    let query = supabase
      .from('auth_verification_sessions')
      .select('*', { count: 'exact' })
      .order('start_timestamp', { ascending: false });
    
    if (statusFilter === 'success') {
      query = query.eq('success', true);
    } else if (statusFilter === 'error') {
      query = query.eq('success', false);
    }
    
    if (labelFilter) {
      query = query.ilike('label', `%${labelFilter}%`);
    }
    
    const { data, error, count } = await query;
    
    if (error) {
      console.error("[VerificationService] Error fetching sessions:", error);
      throw error;
    }
    
    return {
      sessions: data as SessionVerification[],
      totalCount: count || 0
    };
  } catch (error) {
    console.error("[VerificationService] Error in getSessions:", error);
    throw error;
  }
};

// Get steps for a specific session
export const getStepsBySessionId = async (sessionId: string): Promise<VerificationStep[]> => {
  try {
    const { data, error } = await supabase
      .from('auth_verification_steps')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    
    if (error) {
      console.error(`[VerificationService] Error fetching steps for session ${sessionId}:`, error);
      throw error;
    }
    
    return data as VerificationStep[];
  } catch (error) {
    console.error("[VerificationService] Error in getStepsBySessionId:", error);
    throw error;
  }
};

// Purge sessions older than the specified number of days
export const purgeOldSessions = async (daysToKeep: number = 7): Promise<boolean> => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    
    const { error } = await supabase
      .from('auth_verification_sessions')
      .delete()
      .lt('start_timestamp', cutoffDate.toISOString());
    
    if (error) {
      console.error(`[VerificationService] Error purging sessions older than ${daysToKeep} days:`, error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error("[VerificationService] Error in purgeOldSessions:", error);
    throw error;
  }
};

// Clean up incomplete test sessions
export const cleanupIncompleteTestSessions = async (hoursOld: number = 24): Promise<boolean> => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setHours(cutoffDate.getHours() - hoursOld);
    
    const { error } = await supabase
      .from('auth_verification_sessions')
      .delete()
      .eq('success', false)
      .lt('start_timestamp', cutoffDate.toISOString());
    
    if (error) {
      console.error(`[VerificationService] Error cleaning up incomplete sessions older than ${hoursOld} hours:`, error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error("[VerificationService] Error in cleanupIncompleteTestSessions:", error);
    throw error;
  }
};

// Delete all sessions
export const deleteAllSessions = async (): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('auth_verification_sessions')
      .delete()
      .neq('session_id', 'DO_NOT_DELETE'); // This ensures we delete all sessions
    
    if (error) {
      console.error('[VerificationService] Error deleting all sessions:', error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error("[VerificationService] Error in deleteAllSessions:", error);
    throw error;
  }
};
